import {useTranslateForLanguage} from "crqlar-react-common/dist/utils/text.util";
import React, {useEffect, useState} from "react";
import SigningButton from "./SigningButton";
import SigningFrame from "./SigningFrame";
import SigningNotice from "./SigningNotice";
import SigningShell from "./SigningShell";
import SigningSuccessAnimation from "./SigningSuccessAnimation";
import {
    DigitalSigningFailureReason,
    DigitalSigningSession,
    DigitalSigningSnapshot,
    SnapshotHotel,
    SnapshotTheme
} from "./digitalSigning.types";
import DigitalSigningForm from "./DigitalSigningForm";
import {getDigitalSigningSession, isDigitalSigningFailure} from "./publicDigitalSigning.api";
import {currentToken, forgetToken, keepTokenOutOfUrl, tokenFromHash} from "./token.util";

const THANKS_MS = 20000;

type Brand = {theme?: SnapshotTheme, hotel?: SnapshotHotel, language?: string, thankYou?: string};

type View =
    | {kind: 'loading'}
    | {kind: 'loadFailed'}
    | {kind: 'ready', session: DigitalSigningSession}
    | {kind: 'unavailable' | 'changed' | 'thanks' | 'done', brand?: Brand};

const brandOf = ({theme, hotel, language}: DigitalSigningSnapshot): Brand => ({theme, hotel, language});

const browserLanguage = () => (
    typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('de') ? 'de' : 'en'
);

const goneView = (reason: DigitalSigningFailureReason) => (reason === 'CHANGED' ? 'changed' : 'unavailable');

function DigitalSigning({token}: {token?: string}) {
    const [view, setView] = useState<View>(token ? {kind: 'loading'} : {kind: 'unavailable'});
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [pickedLanguage, setPickedLanguage] = useState<string>();
    const {tFor} = useTranslateForLanguage();

    useEffect(() => {
        if (!token) return;

        let cancelled = false;
        setView({kind: 'loading'});

        getDigitalSigningSession(token)
            .then(session => {
                if (!cancelled) setView({kind: 'ready', session});
            })
            .catch(error => {
                if (cancelled) return;

                const reason = isDigitalSigningFailure(error) ? error.reason : 'NETWORK';
                if (reason === 'NETWORK') {
                    setView({kind: 'loadFailed'});
                    return;
                }
                forgetToken();
                setView({kind: goneView(reason)});
            });

        return () => {
            cancelled = true;
        };
    }, [token, loadAttempt]);

    const brand = view.kind === 'ready' ? brandOf(view.session.snapshot) : 'brand' in view ? view.brand : undefined;
    const language = pickedLanguage || brand?.language || browserLanguage();
    const t = tFor(language);

    useEffect(() => {
        const root = document.documentElement;
        const previous = root.lang;
        root.lang = language;

        return () => {
            root.lang = previous;
        };
    }, [language]);

    useEffect(() => {
        if (view.kind !== 'thanks') return;

        const timeout = window.setTimeout(() => setView({kind: 'done', brand: view.brand}), THANKS_MS);
        return () => window.clearTimeout(timeout);
    }, [view]);

    const finish = (kind: 'thanks' | 'unavailable' | 'changed') => {
        forgetToken();
        const snapshot = view.kind === 'ready' ? view.session.snapshot : undefined;
        const thankYou = kind === 'thanks' ? snapshot?.thankYouTexts?.[language]?.trim() || undefined : undefined;
        setView({kind, brand: snapshot ? {...brandOf(snapshot), thankYou} : brand});
        window.scrollTo({top: 0});
    };

    const content = (() => {
        switch (view.kind) {
            case 'loading':
                return <SigningNotice busyLabel={t('Loading')}/>;
            case 'loadFailed':
                return (
                    <SigningNotice
                        title={t('The confirmation could not be loaded.')}
                        description={t('Please check the connection and try again.')}
                        action={(
                            <SigningButton onClick={() => setLoadAttempt(attempt => attempt + 1)}>
                                {t('Try again')}
                            </SigningButton>
                        )}
                    />
                );
            case 'unavailable':
                return (
                    <SigningNotice
                        title={t('This confirmation is no longer available.')}
                        description={t('Please ask staff for a new link.')}
                    />
                );
            case 'changed':
                return (
                    <SigningNotice
                        title={t('The details changed.')}
                        description={t('Please ask staff for a new link.')}
                    />
                );
            case 'thanks':
                return (
                    <SigningNotice
                        visual={<SigningSuccessAnimation/>}
                        title={view.brand?.thankYou || t('Thank you for your confirmation.')}
                    />
                );
            case 'done':
                return (
                    <SigningNotice title={t('Confirmation sent')} description={t('This page can be closed.')}/>
                );
            case 'ready':
                return undefined;
        }
    })();

    return (
        <SigningFrame theme={brand?.theme} page>
            {view.kind === 'ready' ? (
                <DigitalSigningForm
                    token={token ?? ''}
                    session={view.session}
                    language={language}
                    t={t}
                    onLanguageChange={setPickedLanguage}
                    onSigned={() => finish('thanks')}
                    onGone={reason => finish(goneView(reason))}
                    fill
                />
            ) : (
                <SigningShell theme={brand?.theme} hotel={brand?.hotel} body={content} fill/>
            )}
        </SigningFrame>
    );
}

export default function PublicDigitalSigningPage() {
    const [token, setToken] = useState(currentToken);

    useEffect(() => {
        if (token) keepTokenOutOfUrl(token);
    }, [token]);

    useEffect(() => {
        const onHashChange = () => {
            const next = tokenFromHash(window.location.hash);
            if (!next) return;

            keepTokenOutOfUrl(next);
            setToken(next);
        };

        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    return <DigitalSigning key={token ?? ''} token={token}/>;
}
